import axios from "axios";
import storage from "@storage";
import pubSub from "utils/pub-sub";
import i18n from "i18next";
import { apiUrl, archiveApiUrl, apiV2Url, baseURL } from "config/environment";
import { isNewerVersion } from "utils/helpers/versionChecker";

const isAiTranslateUrl = url => !!url && url.includes("ai/_translate");

const isArchiveUrl = url => {
  if (!url) return false;
  return url.startsWith("archives/") || url.includes(archiveApiUrl);
};

const normalizeVersionStorage = value => {
  if (!value) return {};

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return normalizeVersionStorage(JSON.parse(value));
  }

  throw new Error("Invalid version storage");
};

function extractTargetEmail(msg) {
  if (!msg) return;
  const parts = msg.split("'");
  if (parts.length >= 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parts[1])) {
    return parts[1];
  }
  const m = msg.match(/'([^']+@[^']+)'/);
  return m?.[1];
}

const createMeaningfulMessage = err => {
  let errorMessage;
  const response = err?.response;
  let errorCode = response?.data?.message;
  const target = extractTargetEmail(errorCode);

  if (!response) {
    errorMessage = "generic:errors.network";
  } else {
    errorMessage = "generic:errors.unexpected";
  }

  const extendedCode =
    response?.data?.["extended-code"] || response?.headers?.["x-extended-code"];

  if (extendedCode && !i18n.exists(`generic:errors.${errorCode}`)) {
    errorCode = extendedCode.replace(/\./g, "*");
  }

  if (
    response?.status === 504 &&
    isAiTranslateUrl(err?.config?.url) &&
    !i18n.exists(`generic:errors.${errorCode}`)
  ) {
    errorCode = "ai_translate_timeout";
  }

  if (errorCode) {
    const errorKey = `generic:errors.${errorCode}`;
    if (errorCode && i18n.exists(errorKey)) {
      errorMessage = i18n.t(errorKey);
    }
  }

  if (!!target) {
    errorMessage = i18n.t(`generic:errors.${errorCode}`, {
      target
    });
  }

  if (errorCode === "77*182*501*309" || errorCode === "77*147*501*309") {
    const count = response?.data?.message?.split("than ")?.[1];
    errorMessage = i18n.t(`generic:errors.${errorCode}`, { count });
  }
  if (errorCode === "77*182*501*310" || errorCode === "77*147*501*310") {
    const count = response?.data?.message?.split("than ")?.[1];
    errorMessage = i18n.t(`generic:errors.${errorCode}`, { count });
  }

  if (
    typeof errorMessage === "string" &&
    errorMessage.startsWith("generic:") &&
    !i18n.exists(errorMessage)
  ) {
    return errorMessage;
  }

  return i18n.t(errorMessage);
};

const checkVersionNumberDifference = data => {
  try {
    const backendVersion = data?.headers?.["x-api-version"];
    const localVersion = process.env.REACT_APP_VERSION;
    if (!backendVersion || !localVersion || isArchiveUrl(data?.config?.url)) {
      return;
    }

    const version = normalizeVersionStorage(storage?.get("version"));
    const [storedBackendVersion] = Object.keys(version);
    const storedLocalVersion = version[storedBackendVersion];
    const isKnownPair =
      storedBackendVersion === backendVersion &&
      storedLocalVersion === localVersion;

    const isStaleResponse =
      !!storedBackendVersion &&
      isNewerVersion(storedBackendVersion, backendVersion);

    if (!isKnownPair && !isStaleResponse) {
      storage.set("version", { [backendVersion]: localVersion });
    }

    if (!isNewerVersion(backendVersion, localVersion)) {
      return;
    }

    const isAlreadyReported =
      !!storedBackendVersion &&
      storedLocalVersion === localVersion &&
      !isNewerVersion(backendVersion, storedBackendVersion);

    if (isAlreadyReported) {
      return;
    }

    pubSub.trigger("versionChanged");
  } catch (error) {
    storage.remove("version");
  }
};

const axiosFactory = baseURL => {
  const http_client = axios.create({
    baseURL
  });

  http_client.interceptors.request.use(
    config => {
      const token = storage.get("accessToken");
      const deviceId = storage.get("deviceId");

      if (deviceId) {
        config.headers["Device-ID"] = deviceId;
      }

      if (
        config.url.includes("login?host=") &&
        window.location.pathname === "/login"
      ) {
        return config;
      }

      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      error.response.message = createMeaningfulMessage(error);
      return Promise.reject(error);
    }
  );

  http_client.interceptors.response.use(
    response => {
      checkVersionNumberDifference(response);
      return response;
    },

    error => {
      if (
        error?.response?.status === 401 &&
        !error?.request?.responseURL.includes("oauth2")
      ) {
        pubSub.trigger("denied", error?.response);
        const msg = error?.response?.data?.message;
        const isAuthError =
          msg === "Authorization required" ||
          msg === "ERR_AUTHORIZATION_REQUIRED" ||
          msg === "API key expired" ||
          msg === "API key not found";
        if (isAuthError) {
          return new Promise(() => {});
        }
      }

      if (error?.response?.data?.message === "ERR_PASSWORD_MUST_BE_CHANGED") {
        pubSub.trigger("passwordChange");
      }

      if (
        error?.response?.status === 503 &&
        error?.response?.headers?.["x-extended-code"] === "99.0.999.503"
      ) {
        if (window.location.pathname !== "/maintenance") {
          window.location.href = "/maintenance";
        }
      }

      error.errorMessage = createMeaningfulMessage(error);
      return Promise.reject(error);
    }
  );

  http_client.interceptors.request.use(
    req => {
      if (isArchiveUrl(req.url)) {
        req.baseURL = archiveApiUrl;
      }

      return req;
    },
    error => {
      error.errorMessage = createMeaningfulMessage(error);
      return Promise.reject(error);
    }
  );

  return http_client;
};

export const axiosBase = axiosFactory(baseURL);
export const axiosV2 = axiosFactory(apiV2Url);
export default axiosFactory(apiUrl);
